import { useEffect, useRef } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AMD_MODES, AmdMode, IntegrationType, VoiceIntegration } from '@/shared/types/integration';

import PreferenceCheckbox from './PreferenceCheckbox';

// Channels resolves an enabled toggle with no mode to this value (fail-open);
// storing it explicitly keeps the saved config and the dial behavior in sync.
const DEFAULT_AMD_MODE: AmdMode = 'premium_ios_call_screening_detection';

const AMD_MODE_LABEL_KEYS: Record<AmdMode, string> = {
  premium: 'integrations.voice_common.amd_mode_premium',
  premium_ios_call_screening_detection: 'integrations.voice_common.amd_mode_premium_ios',
};

const AMD_MODE_HINT_KEYS: Record<AmdMode, string> = {
  premium: 'integrations.voice_common.amd_mode_premium_hint',
  premium_ios_call_screening_detection: 'integrations.voice_common.amd_mode_premium_ios_hint',
};

/**
 * Answering Machine Detection preference, rendered inside Preferences.
 *
 * Mounted by telnyx-voice and twilio-voice. The mode selector is telnyx-only: channels
 * gates the Twilio dial on `enabled` alone, so a mode there would be dead config.
 */
const AnsweringMachineDetection = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<VoiceIntegration>();

  // Discriminator comes from form context so this card keeps its zero-prop signature.
  const integrationType = useWatch<VoiceIntegration>({ name: 'type' }) as
    IntegrationType | undefined;
  const isTelnyx = integrationType === IntegrationType.TELNYX_VOICE;

  const amdEnabled = useWatch<VoiceIntegration>({ name: 'config.amd.enabled' }) as
    boolean | undefined;
  const amdMode = useWatch<VoiceIntegration>({ name: 'config.amd.mode' }) as AmdMode | undefined;

  // Mode last seen before the toggle was switched off, so an off→on round trip
  // restores it instead of silently resetting a saved 'premium' to the default.
  const previousMode = useRef<AmdMode | undefined>(undefined);

  // Legacy telnyx rows saved as `{ enabled: true }` dial with the channels fail-open default —
  // surface it instead of an empty dropdown. Enable-via-checkbox is handled in `onCheckedChange`.
  useEffect(() => {
    if (isTelnyx && amdEnabled && !amdMode) {
      setValue('config.amd.mode', DEFAULT_AMD_MODE, { shouldDirty: false });
    }
  }, [isTelnyx, amdEnabled, amdMode, setValue]);

  return (
    <>
      <FormField
        control={control}
        name="config.amd.enabled"
        // Registering this field materializes `config.amd`; without a default, an integration
        // that never configured AMD gets `amd: {}` — rejected by `enabled: required()`, 400 by
        // the orchestrator.
        defaultValue={false}
        render={({ field }) => (
          <PreferenceCheckbox
            title={t('integrations.voice_common.amd_title')}
            description={t('integrations.voice_common.amd_description')}
            checked={field.value ?? false}
            onCheckedChange={(checked) => {
              field.onChange(checked);
              if (!isTelnyx) {
                return;
              }
              if (checked && !amdMode) {
                setValue('config.amd.mode', previousMode.current ?? DEFAULT_AMD_MODE, {
                  shouldDirty: true,
                });
              }
              if (!checked) {
                // Clear mode so disabled saves as { enabled: false }, but keep it
                // in the ref so re-enabling restores the user's choice.
                previousMode.current = amdMode;
                setValue('config.amd.mode', undefined, { shouldDirty: true });
              }
            }}
          />
        )}
      />

      {isTelnyx && amdEnabled ? (
        <FormField
          control={control}
          name="config.amd.mode"
          render={({ field }) => {
            const value = field.value as AmdMode | undefined;
            return (
              <FormItem className="pl-7">
                <FormLabel>{t('integrations.voice_common.amd_mode_label')}</FormLabel>
                <FormControl>
                  <Select
                    value={value ?? ''}
                    onValueChange={(next: AmdMode) =>
                      setValue('config.amd.mode', next, { shouldDirty: true })
                    }
                  >
                    <SelectTrigger className="w-full">
                      {/* Trigger renders just the mode name, not the rich label below. */}
                      <SelectValue
                        placeholder={t('integrations.voice_common.amd_mode_placeholder')}
                      >
                        {value ? t(AMD_MODE_LABEL_KEYS[value]) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {AMD_MODES.map((mode) => (
                        <SelectItem
                          key={mode}
                          value={mode}
                          textValue={t(AMD_MODE_LABEL_KEYS[mode])}
                        >
                          <div className="flex flex-col gap-0.5 py-0.5">
                            <span className="font-medium">{t(AMD_MODE_LABEL_KEYS[mode])}</span>
                            <span className="text-xs text-muted-foreground">
                              {t(AMD_MODE_HINT_KEYS[mode])}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      ) : null}
    </>
  );
};

export default AnsweringMachineDetection;
